import React from 'react'
import {
  Sparkles,
  Bug,
  HandHeart,
  Handshake,
  ShieldCheck,
  Megaphone,
  Clock,
  Brain,
  TrendingUp,
} from 'lucide-react'
import type { TranslationKey } from '@/modules/i18n'
import type { UserService } from '@duck/api-definitions'

export type ReleaseNoteLabel =
  | 'feature'
  | 'bugfix'
  | 'challenge'
  | 'partner'
  | 'admin'
  | 'announcement'
  | 'comingsoon'
  | 'ai'
  | 'improvement'

export const RELEASE_NOTE_LABELS: ReleaseNoteLabel[] = [
  'feature',
  'bugfix',
  'improvement',
  'ai',
  'challenge',
  'partner',
  'admin',
  'announcement',
  'comingsoon',
]

export const LABEL_ICONS: Record<ReleaseNoteLabel, React.ReactNode> = {
  feature: <Sparkles className="h-3.5 w-3.5" />,
  bugfix: <Bug className="h-3.5 w-3.5" />,
  challenge: <HandHeart className="h-3.5 w-3.5" />,
  partner: <Handshake className="h-3.5 w-3.5" />,
  admin: <ShieldCheck className="h-3.5 w-3.5" />,
  announcement: <Megaphone className="h-3.5 w-3.5" />,
  comingsoon: <Clock className="h-3.5 w-3.5" />,
  ai: <Brain className="h-3.5 w-3.5" />,
  improvement: <TrendingUp className="h-3.5 w-3.5" />,
}

export const LABEL_BORDER_COLORS: Partial<Record<ReleaseNoteLabel, string>> = {
  feature: 'border-l-blue-500',
  bugfix: 'border-l-amber-500',
  challenge: 'border-l-primary',
  partner: 'border-l-primary',
  admin: 'border-l-purple-500',
  announcement: 'border-l-emerald-500',
  comingsoon: 'border-l-slate-500',
  ai: 'border-l-violet-500',
  improvement: 'border-l-cyan-500',
}

export const LABEL_BADGE_CLASSES: Partial<Record<ReleaseNoteLabel, string>> = {
  feature: 'bg-blue-500/20 text-blue-400 border-0',
  bugfix: 'bg-amber-500/20 text-amber-400 border-0',
  challenge: 'bg-primary/10 text-primary border-0',
  partner: 'bg-primary/90 text-primary-foreground border-0',
  admin: 'bg-purple-500/20 text-purple-300 border-0',
  announcement: 'bg-emerald-500/20 text-emerald-300 border-0',
  comingsoon: 'bg-slate-500/20 text-slate-300 border-0',
  ai: 'bg-violet-500/20 text-violet-300 border-0',
  improvement: 'bg-cyan-500/20 text-cyan-400 border-0',
}

export const LABEL_I18N_KEYS: Record<ReleaseNoteLabel, TranslationKey> = {
  feature: 'releaseNotes.labels.feature',
  bugfix: 'releaseNotes.labels.bugfix',
  challenge: 'releaseNotes.labels.challenge',
  partner: 'releaseNotes.labels.partner',
  admin: 'releaseNotes.labels.admin',
  announcement: 'releaseNotes.labels.announcement',
  comingsoon: 'releaseNotes.labels.comingsoon',
  ai: 'releaseNotes.labels.ai',
  improvement: 'releaseNotes.labels.improvement',
}

export function getLabels(note: UserService.ReleaseNote): ReleaseNoteLabel[] {
  const raw = note.labels
  if (!Array.isArray(raw) || raw.length === 0) return ['feature']
  return raw.filter((l): l is ReleaseNoteLabel =>
    RELEASE_NOTE_LABELS.includes(l as ReleaseNoteLabel)
  )
}
